// ============================================================
// ADAPTIVE JITTER: Data-driven noise profiling
//
// Analyzes a historical time series to compute a JitterProfile
// that reflects the data's actual volatility, significance,
// and edge cases (sparsity, fat tails, trend contamination,
// staleness, intermittent zeros).
//
// The output JitterProfile is consumed by:
//   - Forecast engines (to size noise amplitude + confidence bands)
//   - UI (to set default slider values + show quality badges)
//
// ============================================================
//
// PIPELINE STEPS (inside computeAdaptiveJitter):
//   1. Edge case: <2 data points → stochastic fallback
//   2. Basic statistics: mean, std, coefficient of variation (CV)
//   3. Robust volatility: Median Absolute Deviation (MAD)
//   4. Fat-tail detection: excess kurtosis
//   5. Trend contamination: linear detrend → detrended std
//   6. Sparsity & zero-fraction analysis
//   7. Choose effective sigma (MAD vs detrended vs raw std)
//   8. Bayesian shrinkage toward account prior
//   9. Staleness & intermittent-zero adjustments
//  10. Quality classification → noise/band multipliers
//  11. Default optimism slider computation
//  12. Final output: JitterProfile
//
// ============================================================

import { JitterProfile, DataQuality } from './types';

/**
 * Compute an adaptive jitter profile from historical data.
 *
 * This is the central data-quality analysis function.
 * It handles: sparse data, high/low volatility, outliers,
 * trend contamination, data staleness, and intermittent zeros.
 *
 * @param values            The raw series (e.g. daily ROAS or daily revenue), including zeros
 * @param validValues       Only the non-null/non-zero entries
 * @param totalDays         Total date range span
 * @param daysSinceLastValue Gap since last non-zero observation
 * @param accountWideStd    Account-level volatility prior (for Bayesian shrinkage)
 * @param isRevenueSeries   True for non-paid revenue (absolute values vs ratios)
 * @returns                 A JitterProfile with all noise/band/slider parameters
 */
export const computeAdaptiveJitter = (
    values: number[],
    validValues: number[],
    totalDays: number,
    daysSinceLastValue: number,
    accountWideStd?: number,
    isRevenueSeries?: boolean
): JitterProfile => {
    const n = validValues.length;
    const totalN = values.length;

    // ---- STEP 1: EDGE CASE — Basically no data ----
    // With fewer than 2 data points we can't compute any statistics.
    // Fall back to a high-uncertainty "stochastic" profile.
    if (n < 2) {
        const fallbackSigma = accountWideStd ?? (isRevenueSeries ? 50 : 0.3);
        return {
            noiseMultiplier: 0.4,
            bandWidthMultiplier: 2.5,
            defaultSliderValue: 60,
            qualityLabel: 'stochastic',
            qualityReason: 'Insufficient data (< 2 points)',
            useImpulseNoise: true,
            impulseProbability: 0.10,
            effectiveSigma: fallbackSigma,
            relativeCV: 0.5, // High relative uncertainty for no-data sources
            defaultOptimism: 45, // Slightly pessimistic — no data to justify trend
        };
    }

    // ---- STEP 2: BASIC STATISTICS ----
    const mean = validValues.reduce((a, b) => a + b, 0) / n;
    const safeMean = Math.max(mean, isRevenueSeries ? 1 : 0.01); // Avoid division by zero

    // Standard deviation (sample)
    const variance = validValues.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1);
    const std = Math.sqrt(variance);

    // Coefficient of Variation (CV) — normalized volatility
    const cv = std / safeMean;

    // ---- STEP 3: ROBUST VOLATILITY — Median Absolute Deviation (MAD) ----
    // MAD is more resistant to outliers than standard deviation.
    // The 1.4826 scaling factor makes MAD consistent with std for normal distributions.
    const sorted = [...validValues].sort((a, b) => a - b);
    const median = sorted[Math.floor(n / 2)];
    const absDeviations = validValues.map(v => Math.abs(v - median));
    const mad = absDeviations.sort((a, b) => a - b)[Math.floor(n / 2)] * 1.4826; // Scale to match std

    // ---- STEP 4: FAT-TAIL DETECTION — Kurtosis ----
    // Excess kurtosis > 5 indicates heavy tails (more extreme outliers than normal).
    // When detected, we prefer MAD over std and widen confidence bands.
    const m4 = validValues.reduce((s, v) => s + ((v - mean) / (std || 1)) ** 4, 0) / n;
    const kurtosis = m4; // Excess kurtosis: normal = 3
    const hasFatTails = kurtosis > 5;

    // ---- STEP 5: TREND CONTAMINATION — De-trend before measuring volatility ----
    // A strong upward trend inflates stddev but isn't really "noise".
    // We fit a linear regression and take the std of the residuals instead.
    let detrendedStd = std;
    if (n >= 2) {
        // Simple linear regression to de-trend
        let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
        for (let k = 0; k < n; k++) { sumX += k; sumY += validValues[k]; sumXY += k * validValues[k]; sumXX += k * k; }
        const denom = n * sumXX - sumX * sumX;
        const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
        const intercept = (sumY - slope * sumX) / n;
        const residuals = validValues.map((v, k) => v - (intercept + slope * k));
        const residVar = residuals.reduce((s, r) => s + r ** 2, 0) / (n - 2);
        detrendedStd = Math.sqrt(Math.max(0, residVar));
    }

    // ---- STEP 6: SPARSITY & ZERO-FRACTION ANALYSIS ----
    // Sparsity: what fraction of the date range has NO data?
    // Zero fraction: what fraction of actual values are exactly 0?
    const sparsity = 1 - (n / Math.max(totalN, totalDays, 1));
    const zeroFraction = values.filter(v => v === 0).length / Math.max(totalN, 1);

    // ---- STEP 7: CHOOSE EFFECTIVE SIGMA ----
    // Select the most appropriate volatility measure:
    //   - Fat tails → use MAD (robust to outliers)
    //   - Strong trend → use detrended std (don't confuse trend with noise)
    //   - Otherwise → use raw std
    let effectiveSigma: number;
    if (hasFatTails) {
        effectiveSigma = mad;
    } else if (detrendedStd < std * 0.7 && n > 10) {
        // Trend was inflating std significantly — use detrended
        effectiveSigma = detrendedStd;
    } else {
        effectiveSigma = std;
    }

    // ---- STEP 8: BAYESIAN SHRINKAGE toward account prior ----
    // For sources with few data points, we blend toward the account-level
    // volatility to avoid over-fitting to a tiny sample.
    if (accountWideStd !== undefined) {
        const sigTrust = Math.min(1, n / 15); // Reaches full trust at 15 data points
        effectiveSigma = sigTrust * effectiveSigma + (1 - sigTrust) * accountWideStd;
    }

    // ---- STEP 9a: STALENESS adjustment ----
    // If last observation was >14 days ago, inflate uncertainty
    let stalenessMultiplier = 1;
    if (daysSinceLastValue > 14) {
        stalenessMultiplier = 1 + Math.log(1 + (daysSinceLastValue - 14) / 7) * 0.3;
    }
    effectiveSigma *= stalenessMultiplier;

    // ---- STEP 9b: INTERMITTENT ZEROS (Stability) adjustment ----
    // activityDensity: fraction of total days that have spend.
    // Low density (e.g. ad is on/off) indicates high unreliability.
    const activityDensity = n / Math.max(totalN, 1);
    if (activityDensity < 0.6) {
        // Flickering ad: massive uncertainty boost
        effectiveSigma *= 1 + (0.6 - activityDensity) * 3;
    } else if (zeroFraction > 0.6) {
        effectiveSigma *= 1 + (zeroFraction - 0.6) * 2;
    }

    // ---- STEP 9c: AGE-BASED VOLATILITY inflation ----
    // Newer sources have less data but in ECOMMERCE, sparse data should NOT
    // create massive noise. Keep the penalty gentle.
    if (n < 14) {
        const agePenalty = 1 + (14 - n) * 0.05; // Gentle: max 1.6x for brand new sources
        effectiveSigma *= agePenalty;
    }

    // ---- STEP 10: CLASSIFY into quality tiers ----
    // Based on CV, map to quality label + noise/band parameters
    let quality: DataQuality;
    let reason: string;
    let noiseMultiplier: number;
    let bandWidthMultiplier: number;
    let defaultSlider: number;
    let useImpulse = false;
    let impulseProbability = 0;

    if (n < 2) {
        // Too few data points — stochastic regime
        quality = 'stochastic';
        reason = `Only ${n} data points — high uncertainty`;
        noiseMultiplier = 0.5;
        bandWidthMultiplier = 2.0 + (5 - n) * 0.3;
        defaultSlider = 50 + (5 - n) * 10; // 50-90
        useImpulse = true;
        impulseProbability = 0.12 + (5 - n) * 0.02;
    } else if (cv < 0.15) {
        // Very stable data
        quality = 'excellent';
        reason = `Low volatility (CV: ${(cv * 100).toFixed(0)}%)`;
        noiseMultiplier = 0.6;
        bandWidthMultiplier = 0.8;
        defaultSlider = 70;
    } else if (cv < 0.35) {
        // Normal volatility
        quality = 'good';
        reason = `Moderate volatility (CV: ${(cv * 100).toFixed(0)}%)`;
        noiseMultiplier = 1.0;
        bandWidthMultiplier = 1.0;
        defaultSlider = 100;
    } else if (cv < 0.7) {
        // High volatility
        quality = 'fair';
        reason = `High volatility (CV: ${(cv * 100).toFixed(0)}%)`;
        noiseMultiplier = 1.3;
        bandWidthMultiplier = 1.4;
        defaultSlider = 120;
    } else {
        // Extreme volatility
        quality = 'low';
        reason = `Extreme volatility (CV: ${(cv * 100).toFixed(0)}%)`;
        noiseMultiplier = 0.8; // Reduce noise amplitude to avoid insane swings
        bandWidthMultiplier = 1.8;
        defaultSlider = 80;
        // Switch to impulse for very erratic series
        if (cv > 1.2 || n < 10) {
            useImpulse = true;
            impulseProbability = 0.15;
        }
    }

    // Fat-tail adjustment: widen bands for heavy-tailed distributions
    if (hasFatTails) {
        bandWidthMultiplier *= 1.3;
        reason += ` · fat-tailed (κ=${kurtosis.toFixed(1)})`;
    }

    // Sparsity penalty: if data covers <40% of the date range
    if (sparsity > 0.6) {
        bandWidthMultiplier *= 1 + (sparsity - 0.6);
        reason += ` · sparse (${((1 - sparsity) * 100).toFixed(0)}% coverage)`;
    }

    // Staleness penalty on label/slider
    if (daysSinceLastValue > 14) {
        if (quality === 'excellent') quality = 'good';
        else if (quality === 'good') quality = 'fair';
        reason += ` · stale (${daysSinceLastValue}d ago)`;
        defaultSlider = Math.min(200, defaultSlider + 15);
    }

    // ---- Compute relative CV ----
    // Noise as fraction of signal level — the key for multiplicative noise
    let relativeCV = effectiveSigma / Math.max(safeMean, isRevenueSeries ? 1 : 0.01);

    // DYNAMIC JITTER DAMPENING: 
    // If recent values are crashing toward zero, the absolute noise amplitude should also collapse.
    // The relative CV shouldn't just be based on the historical safeMean, it should adapt to recent reality.
    if (n >= 4) {
        const recentN = Math.max(2, Math.floor(n * 0.2));
        const recentSlice = validValues.slice(-recentN);
        const recentMean = recentSlice.reduce((a, b) => a + b, 0) / recentSlice.length;

        // If the recent average is significantly lower than the historical average
        if (recentMean < safeMean * 0.5) {
            // Smoothly throttle the CV downwards by the crush ratio
            const crushRatio = Math.max(0.1, recentMean / safeMean);
            relativeCV *= crushRatio;
        }
    }

    // ---- STEP 11: COMPUTE DATA-DRIVEN OPTIMISM DEFAULT (0-100, 50 = neutral) ----
    // Based on: trend direction, recent momentum, trend consistency (R²), staleness
    let defaultOptimism = 50; // Start neutral

    if (n >= 2) {
        // 11a. TREND DIRECTION: Normalized slope as % of mean per day
        //      Positive slope → optimistic, negative → pessimistic
        let trendSlope = 0;
        let trendR2 = 0;
        {
            let sx = 0, sy = 0, sxy = 0, sxx = 0;
            for (let k = 0; k < n; k++) { sx += k; sy += validValues[k]; sxy += k * validValues[k]; sxx += k * k; }
            const den = n * sxx - sx * sx;
            trendSlope = den !== 0 ? (n * sxy - sx * sy) / den : 0;
            const intercept = (sy - trendSlope * sx) / n;

            // R² — how consistent is the trend?
            let ssRes = 0, ssTot = 0;
            for (let k = 0; k < n; k++) {
                const predicted = intercept + trendSlope * k;
                ssRes += (validValues[k] - predicted) ** 2;
                ssTot += (validValues[k] - mean) ** 2;
            }
            trendR2 = ssTot > 0 ? Math.max(0, 1 - ssRes / ssTot) : 0;
        }

        // Normalize slope: daily change as fraction of mean
        const normalizedSlope = trendSlope / safeMean;
        // Map to optimism shift: ±0.5%/day ~= ±15 optimism points (clamped)
        const trendShift = Math.max(-15, Math.min(15, normalizedSlope * 3000));

        // 11b. TREND CONSISTENCY (R²): Only lean into the trend if it's reliable
        //      R² > 0.3 = fairly consistent, < 0.1 = noise
        const consistencyWeight = Math.min(1, Math.max(0, (trendR2 - 0.05) / 0.35));

        // 11c. RECENT MOMENTUM: Last 20% of data vs overall mean
        const recentN = Math.max(2, Math.floor(n * 0.2));
        const recentSlice = validValues.slice(-recentN);
        const recentMean = recentSlice.reduce((a, b) => a + b, 0) / recentSlice.length;
        const momentumRatio = recentMean / safeMean; // >1 = accelerating, <1 = decelerating
        // Map: 1.2 → +5, 0.8 → -5
        const momentumShift = Math.max(-8, Math.min(8, (momentumRatio - 1) * 40));

        // 11d. Combine: trend (weighted by consistency) + momentum
        defaultOptimism = 50 + (trendShift * consistencyWeight) + momentumShift;

        // 11e. STALENESS PENALTY: pull toward pessimistic if data is stale
        if (daysSinceLastValue > 7) {
            const stalePull = Math.min(10, (daysSinceLastValue - 7) * 0.5);
            defaultOptimism -= stalePull;
        }

        // 11f. SPARSITY DAMPENING: fewer data points → pull toward 50
        if (n < 15) {
            const dampening = (15 - n) / 15; // 1.0 at n=0, 0.0 at n=15
            defaultOptimism = 50 + (defaultOptimism - 50) * (1 - dampening * 0.6);
        }
    } else {
        // Very few points: slightly pessimistic, not enough data for confidence
        defaultOptimism = 45;
    }

    // Clamp to valid range with a soft ceiling — never extreme
    defaultOptimism = Math.round(Math.max(25, Math.min(75, defaultOptimism)));

    // ---- STEP 12: RETURN FINAL JITTER PROFILE ----
    return {
        noiseMultiplier,
        bandWidthMultiplier,
        defaultSliderValue: Math.round(Math.max(20, Math.min(200, defaultSlider))),
        qualityLabel: quality,
        qualityReason: reason,
        useImpulseNoise: useImpulse,
        impulseProbability,
        effectiveSigma,
        relativeCV: Math.max(0.01, Math.min(2.0, relativeCV)), // Clamp to sane range
        defaultOptimism,
    };
};
